import { useQuery } from '@tanstack/react-query'
import { api } from './client'

// --- Types ---

export interface MetricsSummary {
  spend: number
  sales: number
  orders: number
  impressions: number
  clicks: number
  acos: number
  roas: number
  cpc: number
  ctr: number
  cvr: number
  spendDelta: number
  salesDelta: number
  ordersDelta: number
  impressionsDelta: number
  clicksDelta: number
  acosDelta: number
  roasDelta: number
  cpcDelta: number
  ctrDelta: number
  cvrDelta: number
}

export interface TimeseriesPoint {
  date: string
  spend: number
  sales: number
  acos: number
  roas: number
  clicks: number
  impressions: number
  orders: number
  cpc: number
  ctr: number
}

export interface TopCampaign {
  campaignId: number
  name: string
  adType: string
  state: string
  spend: number
  sales: number
  acos: number
  roas: number
  impressions: number
  clicks: number
  orders: number
  budget: number | null
  budgetUtilization: number | null
}

export interface BudgetAlert {
  campaignId: number
  name: string
  adType: string
  dailyBudget: number
  avgDailySpend7d: number
  utilization: number
  alertLevel: 'ok' | 'warning' | 'critical'
}

export interface MetricsFilters {
  dateFrom: string
  dateTo: string
  adType?: 'sp' | 'sb' | 'sd'
  campaignIds?: number[]
}

// --- Helpers ---

function filtersToParams(filters: MetricsFilters): string {
  const params = new URLSearchParams()
  params.set('dateFrom', filters.dateFrom)
  params.set('dateTo', filters.dateTo)
  if (filters.adType) params.set('adType', filters.adType)
  if (filters.campaignIds?.length) params.set('campaignIds', filters.campaignIds.join(','))
  return params.toString()
}

// --- Hooks ---

export function useMetricsSummary(filters: MetricsFilters) {
  return useQuery({
    queryKey: ['metricsSummary', filters],
    queryFn: () => api.get<{ summary: MetricsSummary }>(`/metrics/summary/?${filtersToParams(filters)}`),
    enabled: !!filters.dateFrom && !!filters.dateTo,
  })
}

export function useMetricsTimeseries(filters: MetricsFilters) {
  return useQuery({
    queryKey: ['metricsTimeseries', filters],
    queryFn: () => api.get<{ timeseries: TimeseriesPoint[] }>(`/metrics/timeseries/?${filtersToParams(filters)}`),
    enabled: !!filters.dateFrom && !!filters.dateTo,
  })
}

export function useTopCampaigns(filters: MetricsFilters & { sortBy?: string; limit?: number }) {
  const params = new URLSearchParams()
  params.set('dateFrom', filters.dateFrom)
  params.set('dateTo', filters.dateTo)
  if (filters.adType) params.set('adType', filters.adType)
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.limit) params.set('limit', String(filters.limit))

  return useQuery({
    queryKey: ['topCampaigns', filters],
    queryFn: () => api.get<{ campaigns: TopCampaign[] }>(`/metrics/top-campaigns/?${params.toString()}`),
    enabled: !!filters.dateFrom && !!filters.dateTo,
  })
}

export function useBudgetMonitor() {
  return useQuery({
    queryKey: ['budgetMonitor'],
    queryFn: () => api.get<{ alerts: BudgetAlert[] }>('/metrics/budget-monitor/'),
    refetchInterval: 60_000,
  })
}
